"use client";

import { useLayoutEffect, useState, type ComponentType } from "react";
import {
  calculateMatching,
  calculateDetailedMatching,
  downloadMatchingReport,
} from "@/lib/api/kundali";
import type { PersonInput, MatchingResponse, DetailedMatchingResponse } from "@/lib/api/kundali";
import type { KundaliRequest } from "@ishvaram/api-client";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { trackCalculatorUsed, trackMatchingCompleted } from "@/lib/analytics-free-tools";
import type { MatchingResultsPanelProps } from "@/components/matching/MatchingResultsPanel";
import { matchingCopy } from "@/components/matching/matching-copy";
import { inspectMatchingResult } from "@/lib/matching-result-quality";
import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";

// Post-submission result rendering lives in the shared MatchingResultsPanel —
// the same component tree the /match/<id> share surface renders (rule 46).
const loadMatchingResultsPanel = () =>
  import("@/components/matching/MatchingResultsPanel").then((mod) => mod.MatchingResultsPanel);

function preloadMatchingResultsPanel() {
  void loadMatchingResultsPanel();
}

function assertCompleteMatchingResult(response: MatchingResponse, fallback: string): void {
  const quality = inspectMatchingResult(response);
  if (quality.complete) return;
  const reason = quality.reasons.join(",");
  reportJsError({
    error_name: "MatchingResultContractViolation",
    error_message: `matching result incomplete: ${reason}`,
    boundary: `KundliMatchingClient.result.${quality.reasons[0] ?? "unknown"}`,
  });
  throw new Error(fallback);
}

function reportMatchingCalculationFailure(error: unknown): void {
  reportJsError({
    ...jsErrorFieldsFromUnknown(error),
    boundary: "KundliMatchingClient.calculation_failed",
  });
}

function personInput(data: KundaliRequest): PersonInput {
  return {
    name: data.name,
    birth_date: data.birth_date,
    birth_time: data.birth_time,
    birth_place: data.birth_place,
    latitude: data.latitude ?? undefined,
    longitude: data.longitude ?? undefined,
    timezone: data.timezone ?? undefined,
  };
}

// Keep the form-to-result reflow below the viewport before paint. A passive
// effect is too late for the post-submit CLS contract (rule 58).
function useResultScrollAnchor(hasResult: boolean) {
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !hasResult) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [hasResult]);
}

export default function KundliMatchingClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const copy = matchingCopy(defaultLanguage);
  const [personAData, setPersonAData] = useState<PersonInput | null>(null);
  const [personBData, setPersonBData] = useState<PersonInput | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchingResponse | null>(null);
  const [ResultsPanel, setResultsPanel] = useState<ComponentType<MatchingResultsPanelProps> | null>(
    null
  );
  const [detailedResult, setDetailedResult] = useState<DetailedMatchingResponse | null>(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedError, setDetailedError] = useState<string | null>(null);

  const [pdfLoading, setPdfLoading] = useState(false);

  // Step tracking: 0 = enter A, 1 = enter B, 2 = results
  const step = result ? 2 : personAData ? 1 : 0;

  useResultScrollAnchor(Boolean(result));

  async function handleSubmitA(data: KundaliRequest) {
    preloadMatchingResultsPanel();
    setPersonAData(personInput(data));
  }

  async function handleSubmitB(data: KundaliRequest) {
    if (!personAData) return;
    const resultsPanelReady = loadMatchingResultsPanel();

    const personB = personInput(data);
    setPersonBData(personB);
    setLoading(true);
    setError(null);

    try {
      const [response, ResultsPanelComponent] = await Promise.all([
        calculateMatching({
          person_a: personAData,
          person_b: personB,
          language: defaultLanguage,
        }),
        resultsPanelReady,
      ]);
      assertCompleteMatchingResult(response, copy.genericError);
      setResultsPanel(() => ResultsPanelComponent);
      setResult(response);
      trackCalculatorUsed({ calculator: "kundli-matching" });
      trackMatchingCompleted({ score: response.total_score ?? 0 });
    } catch (err) {
      reportMatchingCalculationFailure(err);
      setError(customerFacingError(err, defaultLanguage, copy.genericError));
    } finally {
      setLoading(false);
    }
  }

  async function handleGetDetailed() {
    if (!personAData || !personBData) return;
    setDetailedLoading(true);
    setDetailedError(null);

    try {
      const response = await calculateDetailedMatching({
        person_a: personAData,
        person_b: personBData,
        match_id: result?.match_id ?? null,
      });
      setDetailedResult(response);
      trackCalculatorUsed({ calculator: "kundli-matching-detailed" });
    } catch (err) {
      setDetailedError(customerFacingError(err, defaultLanguage, copy.detailedError));
    } finally {
      setDetailedLoading(false);
    }
  }

  async function handleDownloadPdf() {
    if (!personAData || !personBData) return;
    setPdfLoading(true);

    try {
      const blob = await downloadMatchingReport({
        person_a: personAData,
        person_b: personBData,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Matching_Report_${personAData.name}_${personBData.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silently fail for PDF download — user can retry
    } finally {
      setPdfLoading(false);
    }
  }

  function handleReset() {
    setPersonAData(null);
    setPersonBData(null);
    setResult(null);
    setDetailedResult(null);
    setDetailedError(null);
    setError(null);
  }

  return (
    <>
      <MatchingFormSteps
        step={step}
        personAData={personAData}
        loading={loading}
        error={error}
        defaultLanguage={defaultLanguage}
        onSubmitA={handleSubmitA}
        onSubmitB={handleSubmitB}
        onChangePersonA={() => {
          setPersonAData(null);
          setPersonBData(null);
        }}
      />

      {loading && <MatchingLoading language={defaultLanguage} />}
      <MatchingSubmitError error={error} step={step} loading={loading} />
      {result && ResultsPanel && (
        <ResultsPanel
          surface="tool"
          result={result}
          personAData={personAData}
          personBData={personBData}
          defaultLanguage={defaultLanguage}
          detailedResult={detailedResult}
          detailedLoading={detailedLoading}
          detailedError={detailedError}
          pdfLoading={pdfLoading}
          onGetDetailed={handleGetDetailed}
          onDownloadPdf={handleDownloadPdf}
          onReset={handleReset}
        />
      )}
    </>
  );
}

function customerFacingError(error: unknown, language: "en" | "hi", fallback: string): string {
  if (language === "hi") return fallback;
  return error instanceof Error ? error.message : fallback;
}

function MatchingFormSteps({
  step,
  personAData,
  loading,
  error,
  defaultLanguage,
  onSubmitA,
  onSubmitB,
  onChangePersonA,
}: {
  step: number;
  personAData: PersonInput | null;
  loading: boolean;
  error: string | null;
  defaultLanguage: "en" | "hi";
  onSubmitA: (data: KundaliRequest) => void;
  onSubmitB: (data: KundaliRequest) => void;
  onChangePersonA: () => void;
}) {
  if (step === 0) {
    return (
      <div className="max-w-md mx-auto">
        <BirthDetailsForm
          onSubmit={onSubmitA}
          loading={false}
          submitLabel={matchingCopy(defaultLanguage).nextPersonB}
          heading={matchingCopy(defaultLanguage).personA}
          optionalName
          language={defaultLanguage}
          analyticsToolName="kundli-matching"
          analyticsStep="person_a"
          showTrustStrip
        />
      </div>
    );
  }
  if (step !== 1 || !personAData) return null;
  return (
    <div className="space-y-4">
      <PersonSummary
        personAData={personAData}
        onChange={onChangePersonA}
        language={defaultLanguage}
      />
      <BirthDetailsForm
        onSubmit={onSubmitB}
        loading={loading}
        submitLabel={matchingCopy(defaultLanguage).calculate}
        error={error ?? undefined}
        heading={matchingCopy(defaultLanguage).personB}
        optionalName
        language={defaultLanguage}
        analyticsToolName="kundli-matching"
        analyticsStep="person_b"
      />
    </div>
  );
}

function PersonSummary({
  personAData,
  onChange,
  language,
}: {
  personAData: PersonInput;
  onChange: () => void;
  language: "en" | "hi";
}) {
  const copy = matchingCopy(language);
  return (
    <div className="card-sacred p-4 flex items-center justify-between">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ish-text-faint">
          {copy.personA}
        </p>
        <p className="text-[15px] font-bold text-ish-dark">{personAData.name}</p>
        <p className="text-[12px] text-ish-text-muted">
          {personAData.birth_date} &middot; {personAData.birth_place}
        </p>
      </div>
      <button onClick={onChange} className="text-[12px] font-semibold text-ish-accent">
        {copy.change}
      </button>
    </div>
  );
}

function MatchingLoading({ language }: { language: "en" | "hi" }) {
  const copy = matchingCopy(language);
  return (
    <div className="text-center py-12">
      <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto border-ish-accent" />
      <p className="mt-4 text-[14px] text-ish-text-muted">{copy.loading}</p>
      <p className="mt-1 text-[12px] text-ish-text-faint">{copy.loadingDetail}</p>
    </div>
  );
}

function MatchingSubmitError({
  error,
  step,
  loading,
}: {
  error: string | null;
  step: number;
  loading: boolean;
}) {
  if (!error || step !== 1 || loading) return null;
  return (
    <div className="mt-4 text-[13px] rounded-lg p-3.5 text-center text-error bg-error/5 border border-error/20">
      {error}
    </div>
  );
}
